// src-lit/components/product/kd-pantry-alert.ts
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { EVENT_NAMES } from '../../constants/events';
import { SELECTORS } from '../../constants/selectors';

/**
 * @element kd-pantry-alert
 * @description「えらんでKuradashi は他の商品と同時購入できません」の dismissible
 *   インライン・アラートバナー。Liquid 側でカートに非パントリー商品が含まれているときだけ
 *   レンダリングされ、下部固定カートフッターの直上に常駐する。
 *
 *   閉じるボタンクリックで非表示にし（DOM からは消さない）、子商品の + 押下で発火する
 *   `kd-pantry-attention-requested` を購読して再表示する。同時購入不可の根本原因
 *   （カートに非パントリー商品が残っている）が解消されていない間は、操作のたびに
 *   理由を再提示できるようにする。
 *
 *   表示状態は `data-alert-state="visible" | "hidden"` 属性で持ち、見た目（tw:hidden）の
 *   切替は Liquid 側の `tw:data-[alert-state=hidden]:hidden` variant で行う。
 *   こうすることで component が自身の class を直接書き換える wc/no-self-class 違反を避ける。
 */
@customElement('kd-pantry-alert')
export class KdPantryAlert extends LitElement {
  createRenderRoot() {
    return this;
  }

  private boundOnClick = this.onClick.bind(this);

  private boundOnAttentionRequested = this.onAttentionRequested.bind(this);

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('click', this.boundOnClick);
    document.addEventListener(
      EVENT_NAMES.PANTRY.ATTENTION_REQUESTED,
      this.boundOnAttentionRequested,
    );
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('click', this.boundOnClick);
    document.removeEventListener(
      EVENT_NAMES.PANTRY.ATTENTION_REQUESTED,
      this.boundOnAttentionRequested,
    );
  }

  private onClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest(SELECTORS.PANTRY_ALERT.CLOSE)) {
      this.dataset.alertState = 'hidden';
    }
  }

  private onAttentionRequested(): void {
    this.dataset.alertState = 'visible';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-pantry-alert': KdPantryAlert;
  }
}
